import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentThread, GetAgentThreadsResponse } from '@dreamai/types';
import api from '../../api';
import { useThreadReads } from '../../providers/ThreadReadsProvider';
import { useAgent } from '../../contexts/AgentContext';
import ProjectsModal from './ProjectsModal';
import NewProjectModal from './NewProjectModal';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import { ReactComponent as PlusIcon } from '../../icons/plus.svg';
import { getProjectTagIconUrl } from '../../utils/projectTagIcon';
import './projects-section.scss';

const PAGE_SIZE = 3;

interface ProjectSuggestion {
  slug: string;
  title: string;
  image: string;
}

// Fallback starter suggestions used if the API call fails or is still loading.
const FALLBACK_STARTERS: ProjectSuggestion[] = [
  { slug: 'plan-a-trip', title: 'Plan a trip', image: 'plan-a-trip.png' },
  {
    slug: 'sort-your-finances',
    title: 'Sort your finances',
    image: 'sort-your-finances.png',
  },
  {
    slug: 'organise-your-side-hustle',
    title: 'Organise your side hustle',
    image: 'organise-your-side-hustle.png',
  },
  {
    slug: 'hit-your-fitness-goals',
    title: 'Hit your fitness goals',
    image: 'hit-your-fitness-goals.png',
  },
  {
    slug: 'plan-your-family-life',
    title: 'Plan your family life',
    image: 'plan-your-family-life.png',
  },
  {
    slug: 'monitor-my-household-bills',
    title: 'Monitor my household bills',
    image: 'monitor-my-house-hold-bills.png',
  },
];

const STARTER_GRADIENTS = [
  'linear-gradient(180deg, #E9F5FC 0%, #BDE0F5 100%)',
  'linear-gradient(180deg, #FFEEE5 0%, #FFCBB2 100%)',
  'linear-gradient(180deg, #E5FFFF 0%, #B3FFFF 100%)',
];

const ProjectsSection: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { activeAgent } = useAgent();
  const { getAllUnreadThreads } = useThreadReads();

  const [projects, setProjects] = useState<AgentThread[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newModalInitialTitle, setNewModalInitialTitle] = useState<
    string | undefined
  >(undefined);
  const [newModalSuggestionSlug, setNewModalSuggestionSlug] = useState<
    string | undefined
  >(undefined);
  const [starters, setStarters] =
    useState<ProjectSuggestion[]>(FALLBACK_STARTERS);
  const [page, setPage] = useState(0);

  const allProjectsRef = useRef<HTMLButtonElement>(null);

  const agentId = activeAgent?.id;

  const fetchSuggestions = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        success: boolean;
        suggestions: ProjectSuggestion[];
      }>('/project-suggestions', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success && res.data.suggestions.length > 0) {
        setStarters(res.data.suggestions);
      }
    } catch (err) {
      console.error('Failed to load project suggestions:', err);
    }
  }, [getAccessTokenSilently]);

  const fetchProjects = useCallback(async () => {
    if (!agentId) return;
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetAgentThreadsResponse>('/agents/threads', {
        params: {
          agent_id: agentId,
          kind: 'Project',
          excludePinned: 'true',
          excludeShared: 'true',
          excludeSubThreads: 'true',
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      const threads = res.data.success ? res.data.threads : [];
      setProjects(threads.filter((t) => !t.is_archived_thread));
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setInitialLoading(false);
    }
  }, [agentId, getAccessTokenSilently]);

  useEffect(() => {
    fetchProjects();
    fetchSuggestions();
  }, [fetchProjects, fetchSuggestions]);

  // Unread thread IDs computed every render so the order reacts to live
  // Pusher updates from ThreadReadsProvider.
  const unreadThreadIds = useMemo(
    () => new Set(getAllUnreadThreads().map((t) => t.thread_id)),
    [getAllUnreadThreads],
  );

  // Sort: unread first, then most recent user activity (last_updated_at desc).
  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      const aUnread = unreadThreadIds.has(a.id) ? 1 : 0;
      const bUnread = unreadThreadIds.has(b.id) ? 1 : 0;
      if (aUnread !== bUnread) return bUnread - aUnread;
      return (
        new Date(b.last_updated_at).getTime() -
        new Date(a.last_updated_at).getTime()
      );
    });
  }, [projects, unreadThreadIds]);

  const handleSelect = (threadId: string) => {
    window.location.assign(
      `/conversations?thread_id=${encodeURIComponent(threadId)}`,
    );
  };

  // Preview override: append ?empty=1 to the URL to force the empty state
  // even when the user has projects (handy for design tweaks).
  const forceEmpty =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('empty') === '1';
  const isEmpty = forceEmpty || sortedProjects.length === 0;

  // Page over whichever set is currently shown.
  const pagedLength = isEmpty ? starters.length : sortedProjects.length;
  const pageCount = Math.max(1, Math.ceil(pagedLength / PAGE_SIZE));

  // Clamp page if the underlying set shrinks (or we toggle between modes).
  useEffect(() => {
    if (page > pageCount - 1) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const visibleProjects = sortedProjects.slice(
    page * PAGE_SIZE,
    page * PAGE_SIZE + PAGE_SIZE,
  );
  const visibleStarters = starters.slice(
    page * PAGE_SIZE,
    page * PAGE_SIZE + PAGE_SIZE,
  );

  if (!agentId || initialLoading) return null;

  const openNewModal = (title?: string, slug?: string) => {
    setNewModalInitialTitle(title);
    setNewModalSuggestionSlug(slug);
    setShowNewModal(true);
  };

  return (
    <section className='projects-section'>
      <header className='projects-section-header'>
        <div className='projects-section-header-left'>
          <span className='projects-section-title'>Projects</span>
          {!isEmpty && (
            <span className='projects-section-count'>
              {sortedProjects.length}
            </span>
          )}
        </div>
        <div className='projects-section-header-right'>
          {!isEmpty && (
            <button
              ref={allProjectsRef}
              type='button'
              className='projects-section-pill-btn'
              onClick={() => setDrawerOpen((o) => !o)}
            >
              All Projects
            </button>
          )}
          <button
            type='button'
            className='projects-section-pill-btn'
            onClick={() => openNewModal()}
          >
            <PlusIcon className='projects-section-pill-btn-icon' />
            New
          </button>
        </div>
      </header>

      {isEmpty && (
        <p className='projects-section-subtitle'>
          See what your Assistant can do, start a Project or create your own
        </p>
      )}

      {isEmpty ? (
        <div className='projects-section-carousel projects-section-carousel--starters'>
          {visibleStarters.map((starter) => {
            // Cycle gradients across the full starter list so each card's
            // colour stays stable as you page through.
            const idx = starters.findIndex((s) => s.slug === starter.slug);
            const gradient = STARTER_GRADIENTS[idx % STARTER_GRADIENTS.length];
            return (
              <button
                key={starter.title}
                type='button'
                className='projects-section-starter-card'
                style={
                  { '--starter-gradient': gradient } as React.CSSProperties
                }
                onClick={() => openNewModal(starter.title, starter.slug)}
              >
                <img
                  src={`/sample-projects/${starter.image}`}
                  alt=''
                  className='projects-section-starter-image'
                />
                <div className='projects-section-starter-title'>
                  {starter.title}
                </div>
                <span className='projects-section-starter-cta'>
                  Start Project
                </span>
              </button>
            );
          })}
          {Array.from({ length: PAGE_SIZE - visibleStarters.length }).map(
            (_, i) => (
              <div
                key={`starter-spacer-${i}`}
                className='projects-section-starter-card projects-section-starter-card-spacer'
                aria-hidden='true'
              />
            ),
          )}
        </div>
      ) : (
        <>
          <div className='projects-section-carousel'>
            {visibleProjects.map((project) => (
              <button
                key={project.id}
                type='button'
                className='projects-section-card'
                onClick={() => handleSelect(project.id)}
              >
                <div className='projects-section-card-image-wrapper'>
                  <img
                    src={
                      getProjectTagIconUrl(project) ||
                      '/splicons/project-tags/other.png'
                    }
                    alt=''
                    className='projects-section-card-image'
                  />
                </div>
                <div className='projects-section-card-description'>
                  {project.description || 'Untitled Project'}
                </div>
              </button>
            ))}
            {/* Pad with empty slots to keep card widths stable on the last page. */}
            {Array.from({ length: PAGE_SIZE - visibleProjects.length }).map(
              (_, i) => (
                <div
                  key={`spacer-${i}`}
                  className='projects-section-card projects-section-card-spacer'
                  aria-hidden='true'
                />
              ),
            )}
          </div>
        </>
      )}

      {pageCount > 1 && (
        <div className='projects-section-control'>
          <button
            type='button'
            className='projects-section-arrow projects-section-arrow--prev'
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            aria-label='Previous page'
          >
            <ArrowLeftIcon />
          </button>
          <div className='projects-section-tickers'>
            {Array.from({ length: pageCount }).map((_, i) => (
              <span
                key={i}
                className={`projects-section-ticker${
                  i === page ? ' active' : ''
                }`}
              />
            ))}
          </div>
          <button
            type='button'
            className='projects-section-arrow projects-section-arrow--next'
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={page === pageCount - 1}
            aria-label='Next page'
          >
            <ArrowRightIcon />
          </button>
        </div>
      )}

      <ProjectsModal
        open={drawerOpen}
        agentId={agentId}
        onClose={() => setDrawerOpen(false)}
      />

      <NewProjectModal
        isOpen={showNewModal}
        onClose={() => {
          setShowNewModal(false);
          setNewModalInitialTitle(undefined);
          setNewModalSuggestionSlug(undefined);
        }}
        agentId={agentId}
        initialTitle={newModalInitialTitle}
        suggestionSlug={newModalSuggestionSlug}
        onProjectCreated={() => {
          fetchProjects();
        }}
      />
    </section>
  );
};

export default ProjectsSection;
